import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { PageFrame } from '../../components/PageFrame';
import type { AdultAuthService } from '../../platform/auth/adult-auth';
import type { AdultDataService } from '../../platform/supabase/adult-data';
import { accountKindOf, homePathFor, kindCache } from './accountKind';

// Keeps each adult on their own side of the product. Teacher and parent
// accounts are separate (sign-up picks one, never both), but until now
// every teacher page only checked "is anyone signed in?" -- so a parent
// could open Teacher Home, Your students, Analytics etc. (for their home
// school), and a teacher could open Parent Home. This checks the account
// kind too, and sends anyone on the wrong side to their own home screen.
// If the kind can't be worked out, access is refused rather than allowed.
//
// The kind is cached per signed-in user for the life of the page, so
// moving between pages doesn't re-ask the server every time.

type Props = {
  auth: Pick<AdultAuthService, 'restoreSession'>;
  data: Pick<AdultDataService, 'listRoleOptions'>;
  // 'teacher' / 'parent': only that kind of account. 'teacher-or-parent':
  // pages both use (Assign work and its confirmation).
  allow: 'teacher' | 'parent' | 'school' | 'teacher-or-parent';
  children: ReactNode;
};

type Status =
  | { phase: 'checking' }
  | { phase: 'allowed' }
  | { phase: 'redirect'; to: string }
  | { phase: 'error' };

export function RequireRole({ auth, data, allow, children }: Props) {
  const [status, setStatus] = useState<Status>({ phase: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const flag = { active: true };
    const isActive = () => flag.active;
    setStatus({ phase: 'checking' });
    void (async () => {
      try {
        const session = await auth.restoreSession();
        if (!isActive()) return;
        if (!session) {
          setStatus({ phase: 'redirect', to: '/auth/sign-in' });
          return;
        }
        const userId = session.user.id;
        let kind = kindCache.get(userId);
        if (!kind) {
          kind = data.listRoleOptions().then(accountKindOf);
          kindCache.set(userId, kind);
          // A failed lookup must not stick -- the next page tries again.
          kind.catch(() => kindCache.delete(userId));
        }
        const resolved = await kind;
        if (!isActive()) return;
        const allowed =
          allow === 'teacher-or-parent'
            ? resolved === 'teacher' || resolved === 'parent'
            : resolved === allow;
        setStatus(
          allowed
            ? { phase: 'allowed' }
            : { phase: 'redirect', to: homePathFor(resolved) },
        );
      } catch {
        if (isActive()) setStatus({ phase: 'error' });
      }
    })();
    return () => {
      flag.active = false;
    };
  }, [auth, data, allow, attempt]);

  if (status.phase === 'redirect') return <Navigate to={status.to} replace />;
  if (status.phase === 'allowed') return <>{children}</>;
  return (
    <PageFrame
      title={
        status.phase === 'error'
          ? 'Your access could not be checked'
          : 'Checking secure access'
      }
      audience="adult"
    >
      {status.phase === 'error' ? (
        <div className="mt-4 grid gap-3">
          <p role="alert">
            Nothing is shown until your access is confirmed. Check your
            connection and try again.
          </p>
          <button
            type="button"
            onClick={() => {
              setAttempt((current) => current + 1);
            }}
            className="min-h-12 w-fit rounded-lg bg-forest-950 px-5 font-semibold text-white hover:bg-forest-800"
          >
            Try again
          </button>
        </div>
      ) : (
        <p role="status" className="mt-4">
          Restoring your authorised workspace…
        </p>
      )}
    </PageFrame>
  );
}
